import { FC, useCallback, useState } from 'react';
import { Badge, Button, Tooltip } from 'antd';
import { ResultsLayout } from 'packages/polaris/tool/results/results-layout';
import styled from '@emotion/styled';
import { FilterFilled } from '@ant-design/icons';
import { FiltersPanel } from 'packages/omnitag/text-search/filters-panel';
import { useSyncRouteParams } from 'packages/omnitag/hooks/use-sync-route-params';
import { useDoSWRSearchQueriesBasedOnURL } from 'packages/omnitag/api/use-do-swr-search-queries';
import { css } from '@emotion/css';
import { useActiveFilterCount } from 'packages/omnitag/hooks/use-active-filter-count';
import { SelectDropdown } from 'packages/omnitag/select-dropdown';
import { useRecoilValue } from 'recoil';
import { uiThemeAtom } from 'packages/at-shell/state/recoil/ui';
import { SearchTopPanel } from 'packages/polaris/tool/search-top-panel';
import { ZoomedImagePreview } from 'packages/polaris/tool/zoomed-image-preview';
import { useTourRef } from 'packages/omnitag/hooks/use-tour-refs';
import { useRememberSelection } from 'packages/polaris/hooks/use-remember-selection';

export const UPLOADED_IMAGE_EXTRA_HEIGHT = 30;

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  height: calc(100vh - 90px);
`;

const SearchPanel = styled.div<{
  isDark: boolean;
}>`
  width: 100%;
  justify-content: space-between;
  display: flex;
  background: ${({ isDark }): string => (isDark ? '#000000' : '#ffffff')};
  border: 1px solid ${({ isDark }): string => (isDark ? '#222222' : '#eeeeee')};
  padding: 1rem;
  width: 100%;
  overflow: hidden;
  margin-bottom: 0.5rem;
  z-index: 10;
`;

const Page = styled.div`
  padding: 1rem;
  width: 100%;
`;

const buttonsContainer = css`
  display: flex;
  gap: 1rem;
`;

export const PolarisLayout: FC<React.PropsWithChildren<unknown>> = () => {
  const uiTheme = useRecoilValue(uiThemeAtom);
  const curSearchParams = useSyncRouteParams();
  const [filtersPanelIsOpen, setFiltersPanelIsOpen] = useState(false);
  const activeFilterCount = useActiveFilterCount();
  const filterRef = useTourRef<HTMLButtonElement>('filter-button');

  useRememberSelection();
  useDoSWRSearchQueriesBasedOnURL();

  const showDrawer = (): void => {
    setFiltersPanelIsOpen(true);
  };

  const onCloseFilterPanel = useCallback((): void => {
    setFiltersPanelIsOpen(false);
  }, []);

  return (
    <>
      <Page>
        <Layout>
          <SearchPanel
            isDark={uiTheme === 'dark'}
            style={{ flexBasis: '70px' }}
          >
            <div>
              <SearchTopPanel />
              <FiltersPanel
                defaultSearchTerm={curSearchParams}
                open={filtersPanelIsOpen}
                onClose={onCloseFilterPanel}
              />
            </div>
            <div className={buttonsContainer}>
              <SelectDropdown />
              <Badge count={activeFilterCount}>
                <Tooltip
                  title="Apply filters to refine your search results"
                  placement="bottomLeft"
                >
                  <Button type="primary" onClick={showDrawer} ref={filterRef}>
                    Filters
                    <FilterFilled />
                  </Button>
                </Tooltip>
              </Badge>
            </div>
          </SearchPanel>

          <ResultsLayout />
        </Layout>
      </Page>
      <ZoomedImagePreview />
    </>
  );
};

export default PolarisLayout;
